import { apiSlice } from '../apiSlice';
import { MESSAGES } from './chatSessionsMessages';
import { API_TAGS } from '../../constants/api';
import handleFileDownload from '../../utils/misc/handleFileDownload';
import { API_NLP_SERVICE_URL } from '../../constants/env';

export const chatSessionsSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    fetchChatSessions: builder.query({
      query: ({ orgId, urlParams = {} }) => ({
        url: `organizations/${orgId}/chat-sessions`,
        method: 'GET',
        params: {
          ...urlParams
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.FETCH_CHAT_SESSIONS.ERROR
        }
      }),
      providesTags: (result, error, args) => {
        if (error?.error) {
          return [{ type: API_TAGS.CHAT_SESSIONS, id: 'LIST' }];
        }
        return [
          ...result.items.map(({ id }) => ({ type: API_TAGS.CHAT_SESSIONS, id })),
          { type: API_TAGS.CHAT_SESSIONS, id: 'LIST' }
        ];
      }
    }),

    fetchChatSessionInfo: builder.query({
      query: ({ orgId, chatSessionId, urlParams = {} }) => ({
        url: `organizations/${orgId}/chat-sessions/${chatSessionId}`,
        method: 'GET',
        params: {
          ...urlParams,
          includeMessages: true,
          includeLiveChats: true,
          includeTenant: true
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.FETCH_CHAT_SESSION_INFO.ERROR
        }
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          const { data: chatSession } = await queryFulfilled;
          const tenantId = chatSession?.tenant?.id;

          if (!tenantId) {
            return;
          }

          // Fetch automated answers for the tenant
          const automatedAnswersResult = await dispatch(apiSlice.endpoints.fetchAutomatedAnswers.initiate({
            orgId: args.orgId,
            tenantId
          }));

          const automatedAnswersData = automatedAnswersResult.data;
          const automatedAnswersItems = automatedAnswersData?.items || [];

          // Here the cache is updated to include the modifications required to add the intentMatchingName property
          if (automatedAnswersItems.length > 0) {
            dispatch(
              apiSlice.util.updateQueryData('fetchChatSessionInfo', args, (draftChatSession) => {
                if (draftChatSession && draftChatSession.messages) {
                  draftChatSession.messages.forEach((message) => {
                    if (
                      message.intentName
                      && message.category !== 'FALLBACK'
                      && message.type === 'TEXT'
                    ) {
                      const matchingIntent = automatedAnswersItems.find(AA => AA.id === message.intentName);
                      if (matchingIntent) {
                        message.intentMatchingName = { ...matchingIntent.name };
                      }
                    }
                  });
                }
              })
            );
          }
        } catch (error) {
          return error;
        }
      },
      providesTags: (result, error, args) => {
        return [{ type: API_TAGS.CHAT_SESSIONS, id: args.chatSessionId }];
      }
    }),

    fetchContactChatSessions: builder.query({
      query: ({ orgId, handle, urlParams = {} }) => ({
        url: `organizations/${orgId}/chat-sessions/subscribers/handle/${handle}`,
        method: 'GET',
        params: {
          ...urlParams
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.FETCH_CHAT_SESSIONS.ERROR
        }
      })
    }),

    updateChatSession: builder.mutation({
      query: ({ orgId, chatSessionId, data, urlParams = {} }) => ({
        url: `organizations/${orgId}/chat-sessions/${chatSessionId}`,
        method: 'PATCH',
        body: data,
        params: {
          ...urlParams
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.UPDATE_CHAT_SESSION.ERROR
        },
        successMessage: {
          showMessage: true,
          messages: MESSAGES.UPDATE_CHAT_SESSION.SUCCESS
        }
      }),
      transformErrorResponse: (response) => false,
      transformResponse: (response) => true,
      invalidatesTags: (result, error, args) => {
        return [{ type: API_TAGS.CHAT_SESSIONS, id: args.chatSessionId }];
      }
    }),

    downloadChatSessions: builder.query({
      query: ({ orgId, urlParams = {} }) => ({
        url: `organizations/${orgId}/chat-sessions/export`,
        method: 'GET',
        params: {
          ...urlParams
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.DOWNLOAD_CHAT_SESSIONS.ERROR
        },
        responseType: 'blob',
        includeResponseHeaders: true
      }),
      transformResponse: (response, meta, arg) => {
        let file = response;
        const fileName = file?.headers['content-disposition']?.split(new RegExp(['"'], 'g'))?.[1] ?? `chat_sessions.${arg?.urlParams?.fileType}`;
        handleFileDownload(file?.responseContents, fileName);
        return {};
      }
    }),

    downloadChatSession: builder.query({
      query: ({ orgId, chatSessionId, urlParams = {} }) => ({
        url: `organizations/${orgId}/chat-sessions/export/${chatSessionId}`,
        method: 'GET',
        params: {
          ...urlParams
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.DOWNLOAD_CHAT_SESSION.ERROR
        },
        responseType: 'blob',
        includeResponseHeaders: true
      }),
      transformResponse: (response, meta, arg) => {
        let file = response;
        const fileName = file?.headers['content-disposition']?.split(new RegExp(['"'], 'g'))?.[1] ?? `${arg.chatSessionId}.${arg.urlParams.fileType}`;
        handleFileDownload(file?.responseContents, fileName);
        return {};
      }
    }),

    generateAISummary: builder.mutation({
      query: ({ orgId, tenantId, chatSessionId }) => ({
        url: `organizations/${orgId}/tenants/${tenantId}/sessions/${chatSessionId}/analyze`,
        method: 'POST',
        API_URL: API_NLP_SERVICE_URL,
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.GENERATE_SUMMARY.ERROR
        }
      }),
      invalidatesTags: (result, error, args) => {
        if (error) {
          return [];
        }
        return[{ type: API_TAGS.CHAT_SESSIONS, id: args.chatSessionId }];
      }
    })
  })
});

export const {
  useFetchChatSessionsQuery,
  useFetchContactChatSessionsQuery,
  useFetchChatSessionInfoQuery,
  useLazyFetchChatSessionInfoQuery,
  useLazyDownloadChatSessionsQuery,
  useLazyDownloadChatSessionQuery,
  useUpdateChatSessionMutation,
  useGenerateAISummaryMutation
} = chatSessionsSlice;
